import React, { useEffect, useCallback } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';

/**
 * Lightbox minimaliste :
 *   - photos : [{ file, caption, description }]
 *   - index  : photo actuellement affichée
 *   - onClose, onPrev, onNext : callbacks
 *   - Navigation clavier : ← → Esc
 */
export default function Lightbox({ photos, index, onClose, onPrev, onNext }) {
  const current = photos?.[index];

  const handleKey = useCallback((e) => {
    if (e.key === 'Escape') onClose();
    else if (e.key === 'ArrowRight') onNext();
    else if (e.key === 'ArrowLeft') onPrev();
  }, [onClose, onNext, onPrev]);

  useEffect(() => {
    if (current) {
      document.body.style.overflow = 'hidden';
      window.addEventListener('keydown', handleKey);
    }
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', handleKey);
    };
  }, [current, handleKey]);

  if (!current) return null;

  return (
    <div
      className="cmt-lightbox"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      data-testid="cmt-lightbox"
    >
      <button
        className="cmt-lightbox-btn cmt-lightbox-close"
        onClick={onClose}
        aria-label="Fermer"
        data-testid="lightbox-close"
      >
        <X size={22} />
      </button>

      <button
        className="cmt-lightbox-btn cmt-lightbox-prev"
        onClick={(e) => { e.stopPropagation(); onPrev(); }}
        aria-label="Photo précédente"
        data-testid="lightbox-prev"
      >
        <ChevronLeft size={30} />
      </button>

      <figure className="cmt-lightbox-figure" onClick={(e) => e.stopPropagation()}>
        <img src={`/photos/${current.file}.jpg`} alt={current.caption} />
        <figcaption>
          <strong>{current.caption}</strong>
          {current.description && <span>{current.description}</span>}
          <em>{index + 1} / {photos.length}</em>
        </figcaption>
      </figure>

      <button
        className="cmt-lightbox-btn cmt-lightbox-next"
        onClick={(e) => { e.stopPropagation(); onNext(); }}
        aria-label="Photo suivante"
        data-testid="lightbox-next"
      >
        <ChevronRight size={30} />
      </button>
    </div>
  );
}
